import { useCallback, useEffect, useState } from 'react'

import { Flex } from '@react-css/flex'
import { Grid } from '@react-css/grid'
import axios from 'axios'

import { useBuildingContext } from '../../../../contexts/buildingContext'
import { useNotifications } from '../../../../contexts/notificationProvider'
import { globalErrorHandler } from '../../../../globalErrorHandler'
import { ViewerPosition } from '../types'

const API_BASE = process.env.REACT_APP_NEXTERA_API

interface DebugPanelProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper imageData type
  imageData: any
  viewerPosition: ViewerPosition
  onOffsetChange?: (newOffset: number) => void
  /** Sync master/subordinate + building image state after a successful PATCH. */
  onAngleSaved?: (nextAngle: number) => void
}

export const DebugPanel = ({
  viewerPosition,
  imageData,
  onOffsetChange,
  onAngleSaved,
}: DebugPanelProps) => {
  const { state: buildingState } = useBuildingContext()
  const { addNotification } = useNotifications()
  const [offsetAngle, setOffsetAngle] = useState<number>(
    () => imageData?.data?.angle ?? 0,
  )

  const notifyOffset = useCallback(
    (value: number) => {
      onOffsetChange?.(value)
    },
    [onOffsetChange],
  )

  useEffect(() => {
    notifyOffset(offsetAngle)
  }, [offsetAngle, notifyOffset])

  useEffect(() => {
    setOffsetAngle(imageData.data.angle)
  }, [imageData.data.id, imageData.data.angle])

  const updateAngle = () => {
    const nextAngle = angleLimit(offsetAngle)
    axios
      .patch(
        `${API_BASE}/project/${buildingState.projectId}/floor/${buildingState.floorId}/viewpoints/image/${imageData.data.id}`,
        { angle: nextAngle },
      )
      .then(() => {
        addNotification('Panorama angle saved', 'success')
        onAngleSaved?.(nextAngle)
      })
      .catch(err => {
        globalErrorHandler(err)
        addNotification('Could not save panorama angle', 'error')
      })
  }

  const savedAngle = imageData.data.angle

  const bump = useCallback((delta: number) => {
    setOffsetAngle(prev => angleLimit(prev + delta))
  }, [])

  return (
    <Flex
      style={{
        flexDirection: 'column',
        position: 'fixed',
        bottom: 0,
        left: 0,
        maxWidth: 360,
        zIndex: 10,
        background: '#111',
        color: '#cfcfcf',
        padding: '12px 14px',
        gap: 10,
        fontSize: 13,
        lineHeight: 1.35,
        opacity: 0.92,
        borderTopRightRadius: 8,
        boxShadow: '0 -2px 12px rgba(0,0,0,0.35)',
      }}
    >
      <div style={{ fontWeight: 600, color: '#fff' }}>
        Panorama floor plan angle
      </div>
      <p style={{ margin: 0, color: '#aaa', fontSize: 12 }}>
        Rotates how the panorama aligns to the floor plan. The minimap cone,
        blue viewpoint dots, and the panorama use this one angle so they stay in
        sync. Change the angle until the scene matches, then save.
      </p>

      <Grid
        columns="minmax(0,1fr) minmax(0,1.2fr)"
        gap="6px 12px"
        style={{ width: '100%' }}
      >
        <Grid.Item
          style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
        >
          {imageData.data.id}
        </Grid.Item>
        <Grid.Item>Image ID</Grid.Item>

        <Grid.Item
          style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
        >
          {imageData.data.height ?? '—'}
        </Grid.Item>
        <Grid.Item>Camera height (in.)</Grid.Item>

        <Grid.Item
          style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
        >
          {savedAngle}°
        </Grid.Item>
        <Grid.Item>Saved angle (last stored)</Grid.Item>

        <Grid.Item
          style={{
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
            color: '#7dffb3',
            fontWeight: 600,
          }}
        >
          {offsetAngle}°
        </Grid.Item>
        <Grid.Item>Editing angle (preview + save)</Grid.Item>

        <Grid.Item
          style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
        >
          {viewerPosition?.yaw?.toFixed(1)}°
        </Grid.Item>
        <Grid.Item>Camera yaw (look direction)</Grid.Item>

        <Grid.Item
          style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
        >
          {viewerPosition?.pitch?.toFixed(1)}°
        </Grid.Item>
        <Grid.Item>Camera pitch</Grid.Item>
      </Grid>

      <Flex style={{ alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <AngleStepButton label="−15°" onClick={() => bump(-15)} />
        <AngleStepButton label="−1°" onClick={() => bump(-1)} />
        <AngleStepButton label="+1°" onClick={() => bump(1)} />
        <AngleStepButton label="+15°" onClick={() => bump(15)} />
        <button
          type="button"
          onClick={updateAngle}
          style={{
            cursor: 'pointer',
            marginLeft: 4,
            padding: '6px 12px',
            backgroundColor: '#1a3d2e',
            color: '#fff',
            border: '1px solid #3d8b6e',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Save to server
        </button>
      </Flex>
    </Flex>
  )
}

function AngleStepButton({
  label,
  onClick,
}: {
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Adjust angle ${label}`}
      style={{
        cursor: 'pointer',
        minWidth: 44,
        padding: '6px 8px',
        backgroundColor: '#222',
        color: '#e8e8e8',
        border: '1px solid #555',
        borderRadius: 6,
        fontSize: 13,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {label}
    </button>
  )
}

const angleLimit = (angle: number): number => {
  if (angle > 360) {
    return angleLimit(angle - 360)
  }
  if (angle < 0) {
    return angleLimit(angle + 360)
  }
  return angle
}
